<template>
	<div class="quest-box-header">
		<div
			class="quest-box-type"
			:class="`icon-quest-${type.toLocaleLowerCase()}-70`"
		/>
		<div
			class="quest-box-titles"
			:class="{
				'quest-box-titles--full': !showPin,
				'quest-box-titles--pinned': showPin,
			}"
		>
			<ShrinkText
				:text="title"
				:fontSize="20"
				:maxLineLimit="2"
				:textAlign="TextAlign.LEFT"
				isBold
			/>
			<span>{{ questTypeText }}</span>
		</div>

		<div
			v-if="showPin"
			class="quest-box-pin"
			:class="isPinned ? 'icon-pinned-48' : 'unpinned'"
			@click="togglePin"
		>
			<div v-if="!isPinned" class="icon-unpinned-24" />
		</div>
	</div>
</template>

<script lang="ts" setup>
import ShrinkText from "@/components/ShrinkText.vue";
import { QuestTypes } from "@/game/entities/config/quests/quest_types.ts";
import useQuestStore from "@/store/useQuestStore";
import { QuestPinStatus } from "@/types/quest";
import { TextAlign } from "@/types/ui.ts";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed, onMounted, ref, watch } from "vue";

const isPinned = ref<boolean>(false);

const questStore = useQuestStore();
const { trackedQuestId } = storeToRefs(questStore);

const emit = defineEmits(["pinAction"]);

interface Props {
	type: QuestTypes;
	title: string;
	showPin?: boolean;
	questId?: number;
}

const { type, title, showPin = false, questId = null } = defineProps<Props>();

const questTypeText = computed((): string => {
	switch (type) {
		case QuestTypes.MAIN:
			return translate("ui.story_quest");
		case QuestTypes.SIDE:
			return translate("ui.side_quest");
		default:
			return translate("ui.story_quest");
	}
});

const togglePin = (): void => {
	isPinned.value = !isPinned.value;

	let pinStatus = "";

	// TODO: doplnit stav `Tracked quest changed`
	if (isPinned.value) {
		pinStatus = QuestPinStatus.PINNED;
	} else {
		pinStatus = QuestPinStatus.UNPINNED;
	}

	emit("pinAction", { pinStatus: pinStatus });
};

onMounted((): void => {
	if (questId === trackedQuestId.value) {
		isPinned.value = true;
	}
});
watch(
	(): number | null => trackedQuestId.value,
	(newId: number | null): void => {
		isPinned.value = questId === newId;
	},
	{ immediate: true },
);
</script>

<style lang="scss" scoped>
.quest-box-header {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	@include flex-row-start;
	padding-right: 1rem;
	padding-bottom: 0.75rem;
	padding-left: 5rem;

	.quest-box-type {
		position: absolute;
		top: -0.75rem;
		left: 0;
		filter: drop-shadow(0.044rem 0.044rem 0 rgba(0, 0, 0, 0.6));
	}

	.quest-box-titles {
		display: flex;
		flex-direction: column;
		align-items: start;
		width: 100%;
		color: #372615 !important;
		font-size: 0.875rem;
		line-height: 1.3rem;
		text-shadow: none !important;
		text-transform: uppercase;

		&--full {
			width: 100%;
		}

		&--pinned {
			padding-right: 3rem;
		}

		.text-container {
			width: 100%;
		}
	}
	.quest-box-pin {
		position: absolute;
		top: 0;
		right: 0;
		cursor: pointer;

		&.unpinned {
			@include flex-row-center;
			top: 0.25rem;
			right: 0.25rem;
			box-shadow: inset 0 0 8px 0 rgba(0, 0, 0, 0.3);
			border-width: 0.075rem;
			border-style: solid;
			border-image-source: linear-gradient(to top, #bd9032, #f0d386);
			border-image-slice: 1;
			background-image: linear-gradient(to top, #181a1d, #303239);
			width: 2.313rem;
			height: 2.313rem;
		}
	}
}
</style>
